// Tenant validators

import { z } from 'zod';
import { emailSchema, phoneSchema, currencyCodeSchema } from '../utils/validation';

// Create tenant validator
export const createTenantSchema = z.object({
  name: z
    .string()
    .min(2, 'Tenant name must be at least 2 characters')
    .max(100, 'Tenant name cannot exceed 100 characters'),
  businessName: z
    .string()
    .min(2, 'Business name must be at least 2 characters')
    .max(200, 'Business name cannot exceed 200 characters'),
  taxId: z.string().optional(),
  address: z.string().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Primary color must be a valid hex color')
    .optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Accent color must be a valid hex color')
    .optional(),
  fontFamily: z.string().optional(),
  channelIdentities: z
    .object({
      name: z.string().min(1, 'Channel name is required'),
      email: emailSchema.optional(),
      phone: phoneSchema.optional(),
    })
    .optional(),
});

// Update tenant validator
export const updateTenantSchema = z.object({
  name: z
    .string()
    .min(2, 'Tenant name must be at least 2 characters')
    .max(100, 'Tenant name cannot exceed 100 characters')
    .optional(),
  businessName: z
    .string()
    .min(2, 'Business name must be at least 2 characters')
    .max(200, 'Business name cannot exceed 200 characters')
    .optional(),
  taxId: z.string().optional(),
  address: z.string().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Primary color must be a valid hex color')
    .optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Accent color must be a valid hex color')
    .optional(),
  fontFamily: z.string().optional(),
  isActive: z.boolean().optional(),
  features: z.array(z.string()).optional(),
  settings: z
    .object({
      paymentTerms: z
        .object({
          paymentDueDays: z.number().int().min(1).max(365).optional(),
          currency: currencyCodeSchema.optional(),
          paymentMethods: z.array(z.string()).optional(),
          creditLimit: z.number().positive().optional(),
        })
        .optional(),
      taxConfiguration: z
        .object({
          taxpayerType: z.enum(['corporate', 'personal']).optional(),
          ppnEnabled: z.boolean().optional(),
          ppnRate: z.number().min(0).max(100).optional(),
          pph23Enabled: z.boolean().optional(),
          pph23Rate: z.number().min(0).max(100).optional(),
          pph21Enabled: z.boolean().optional(),
          pph21Rate: z.number().min(0).max(100).optional(),
        })
        .optional(),
    })
    .optional(),
  channelIdentities: z
    .object({
      name: z.string().min(1, 'Channel name is required'),
      email: emailSchema.optional(),
      phone: phoneSchema.optional(),
    })
    .optional(),
});

// Tenant settings validator
export const tenantSettingsSchema = z.object({
  primaryColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Primary color must be a valid hex color')
    .optional(),
  accentColor: z
    .string()
    .regex(/^#[0-9A-F]{6}$/i, 'Accent color must be a valid hex color')
    .optional(),
  fontFamily: z.string().optional(),
  paymentTerms: z
    .object({
      paymentDueDays: z.number().int().min(1).max(365),
      currency: currencyCodeSchema,
      paymentMethods: z.array(z.string()),
      creditLimit: z.number().positive().optional(),
    })
    .optional(),
  taxConfiguration: z
    .object({
      taxpayerType: z.enum(['corporate', 'personal']).optional(),
      ppnEnabled: z.boolean(),
      ppnRate: z.number().min(0).max(100),
      pph23Enabled: z.boolean(),
      pph23Rate: z.number().min(0).max(100),
      pph21Enabled: z.boolean(),
      pph21Rate: z.number().min(0).max(100),
    })
    .optional(),
});

// Channel identities validator
export const channelIdentitiesSchema = z.object({
  name: z
    .string()
    .min(1, 'Channel name is required')
    .max(100, 'Channel name cannot exceed 100 characters'),
  email: emailSchema.optional(),
  phone: phoneSchema.optional(),
});

// Types
export type CreateTenantInput = z.infer<typeof createTenantSchema>;
export type UpdateTenantInput = z.infer<typeof updateTenantSchema>;
export type TenantSettingsInput = z.infer<typeof tenantSettingsSchema>;
export type ChannelIdentitiesInput = z.infer<typeof channelIdentitiesSchema>;
